'use client';

import React, { useCallback, useMemo, useState } from 'react';

import { Label } from '@components/Common';
import { ErrorLabel, InputContainer } from '@components/Input/styles';

import { Colon, IconWrapper, SelectField, TimeSelectWrapper } from './styles/styles';

interface TimeInputProps {
  id: string;
  name: string;
  label?: string;
  placeholder?: string;
  value?: string;
  hasError?: boolean;
  errorLabel?: string;
  icon?: React.ReactNode;
  hasIcon?: boolean;
  required?: boolean;
  disabled?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const MIN_HOUR = 6;
const MAX_HOUR = 19;
const HOURS = Array.from({ length: MAX_HOUR - MIN_HOUR + 1 }, (_, i) =>
  String(i + MIN_HOUR).padStart(2, '0'),
);
const MINUTE_STEP = 5;
const MINUTES = Array.from({ length: 60 / MINUTE_STEP }, (_, i) =>
  String(i * MINUTE_STEP).padStart(2, '0'),
);

export const TimeInput: React.FC<TimeInputProps> = ({
  id,
  name,
  label,
  placeholder = '00:00',
  value = '',
  hasError,
  errorLabel,
  icon,
  hasIcon,
  required,
  disabled,
  onChange,
  onBlur,
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const { hours, minutes } = useMemo(() => {
    if (value && value.includes(':')) {
      const [h, m] = value.split(':');
      return { hours: h || '', minutes: m || '' };
    }
    return { hours: '', minutes: '' };
  }, [value]);

  const [placeholderHours, placeholderMinutes] = useMemo(() => {
    const parts = placeholder.split(':');
    return [parts[0] || 'HH', parts[1] || 'MM'];
  }, [placeholder]);

  const emitChange = useCallback(
    (newHours: string, newMinutes: string) => {
      if (!onChange) return;
      const timeValue =
        newHours || newMinutes
          ? `${newHours || '00'}:${newMinutes || '00'}`
          : '';

      const syntheticEvent = {
        target: {
          name,
          value: timeValue,
          type: 'time',
          checked: false,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      onChange(syntheticEvent);
    },
    [onChange, name],
  );

  const handleHourChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      emitChange(e.target.value, minutes);
    },
    [emitChange, minutes],
  );

  const handleMinuteChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      emitChange(hours, e.target.value);
    },
    [emitChange, hours],
  );

  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLSelectElement>) => {
      setIsFocused(false);
      if (onBlur) {
        onBlur(e as unknown as React.FocusEvent<HTMLInputElement>);
      }
    },
    [onBlur],
  );

  return (
    <InputContainer>
      {label && <Label htmlFor={`${id}-hours`}>{label}</Label>}
      {hasError && <ErrorLabel id={`${id}-error`}>{errorLabel}</ErrorLabel>}
      <TimeSelectWrapper $hasError={hasError} $isFocused={isFocused}>
        {icon && (
          <IconWrapper $isFocused={isFocused}>
            {icon}
          </IconWrapper>
        )}
        <SelectField
          id={`${id}-hours`}
          aria-label="Hours"
          aria-describedby={hasError ? `${id}-error` : undefined}
          value={hours}
          onChange={handleHourChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          $hasIcon={hasIcon}
          $hasError={hasError}
          $isFocused={isFocused}
        >
          <option value="" disabled>
            {placeholderHours}
          </option>
          {HOURS.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </SelectField>
        <Colon $hasError={hasError} $isFocused={isFocused}>:</Colon>
        <SelectField
          id={`${id}-minutes`}
          aria-label="Minutes"
          aria-describedby={hasError ? `${id}-error` : undefined}
          value={minutes}
          onChange={handleMinuteChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          $hasError={hasError}
          $isFocused={isFocused}
        >
          <option value="" disabled>
            {placeholderMinutes}
          </option>
          {MINUTES.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </SelectField>
      </TimeSelectWrapper>
    </InputContainer>
  );
};
